import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { SlettBlokkering } from '@origo/planregister';
import { Button, Dialog, Dropdown, Loader, Tooltip } from '@origo/origo-ui';
import { DotsThreeIcon, TrashIcon } from '@phosphor-icons/react';
import styles from './PlanActions.module.css';

/** Tilstand for «Slett plan»-dialogen (#14744). */
export interface PlanDeleteDialogState {
    open: boolean;
    /** Slettegrunnlaget hentes når dialogen åpnes. */
    loading: boolean;
    blokkering: SlettBlokkering | null;
    navn: string;
    behandlinger: number;
    dokumenter: number;
    isDeleting: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

interface PlanActionsProps {
    onDeleteRequest: () => void;
    deleteDialog: PlanDeleteDialogState;
}

function blockedBody(t: TFunction, blokkering: SlettBlokkering): string {
    switch (blokkering.arsak) {
        case 'planforhold':
            return t('planDetail.delete.blockedPlanforhold', { count: blokkering.antall });
        case 'dispensasjoner':
            return t('planDetail.delete.blockedDispensasjoner', { count: blokkering.antall });
        case 'vedtatt':
            return t('planDetail.delete.blockedVedtatt');
    }
}

/**
 * Handlinger for hele planen, i «…»-menyen i topplinja. Sletting er sjelden
 * (kun feilregistreringer), så den ligger bak menyen i stedet for som en
 * synlig rød knapp på hver fane.
 */
const PlanActions: React.FC<PlanActionsProps> = ({ onDeleteRequest, deleteDialog }) => {
    const { t } = useTranslation();
    const blocked = deleteDialog.blokkering !== null;

    return (
        <>
            <Tooltip content={t('planDetail.moreActions')} position="left">
                <Dropdown
                    align="end"
                    className={styles.menu}
                    trigger={
                        <span aria-label={t('planDetail.moreActions')}>
                            <DotsThreeIcon size={20} weight="bold" />
                        </span>
                    }
                    groups={[
                        {
                            items: [
                                {
                                    id: 'delete',
                                    label: t('planDetail.delete.action'),
                                    icon: <TrashIcon size={16} weight="duotone" />,
                                    danger: true,
                                    onClick: onDeleteRequest,
                                },
                            ],
                        },
                    ]}
                />
            </Tooltip>
            <Dialog
                open={deleteDialog.open}
                onClose={deleteDialog.onCancel}
                title={t(blocked ? 'planDetail.delete.blockedTitle' : 'planDetail.delete.title')}
                footer={
                    <div className={styles.dialogFooter}>
                        {blocked ? (
                            <Button variant="secondary" onClick={deleteDialog.onCancel}>
                                {t('common.close')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="secondary"
                                    onClick={deleteDialog.onCancel}
                                    disabled={deleteDialog.isDeleting}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={deleteDialog.onConfirm}
                                    disabled={deleteDialog.loading || deleteDialog.isDeleting}
                                >
                                    {t('planDetail.delete.confirm')}
                                </Button>
                            </>
                        )}
                    </div>
                }
            >
                {deleteDialog.loading ? (
                    <Loader label={t('common.loading')} />
                ) : deleteDialog.blokkering ? (
                    <p>{blockedBody(t, deleteDialog.blokkering)}</p>
                ) : (
                    <>
                        <p>
                            {t('planDetail.delete.body', {
                                navn: deleteDialog.navn,
                                behandlinger: deleteDialog.behandlinger,
                                dokumenter: deleteDialog.dokumenter,
                            })}
                        </p>
                        <p>{t('planDetail.delete.hint')}</p>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default PlanActions;
